import { SparkleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentContainer } from '@/context/agent-container.context';
import { useAgentPanelEnabled } from './useAgentPanelEnabled';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { useHotkeyLabel } from '@/hooks/useHotkeyLabel';
import { HOTKEY_IDS } from '@/lib/hotkeys/registry';
import { HELP_MENU_ORDER, HelpMenuSlot } from './HelpMenu';

/**
 * The sidebar Help-menu entry for Nami. Mounted once at the authenticated
 * layout so it is always discoverable. The panel itself is `NamiPanel`; a page
 * opts into page-specific assistance with `useAgentModule()`, and the
 * always-available `home` fallback covers every other page when the Nami
 * copilot is on (chat agent or copilot flag). Disabled only when neither
 * gives the panel a surface.
 */
export function FloatingAgentPanel() {
  const gt = useGT();
  const { isOpen, toggle } = useAgentPanel();
  const { effectiveModuleConfig } = useAgentContainer();
  const enabled = useAgentPanelEnabled(effectiveModuleConfig);

  const hasSurface = effectiveModuleConfig !== null;
  const panelActive = enabled && hasSurface && isOpen;
  const { label: shortcutLabel } = useHotkeyLabel(HOTKEY_IDS.aiAssistant);

  if (!enabled) return null;

  const enabledTooltip = gt('Open AI assistant ({shortcut})', {
    context:
      'tooltip on the collapsed sidebar AI assistant menu item, includes keyboard shortcut',
    shortcut: shortcutLabel,
  });
  const disabledTooltip = gt('AI assistant is not available on this page', {
    context:
      'tooltip explaining that the AI assistant panel has no assistant surface on the current page',
  });

  return (
    <HelpMenuSlot order={HELP_MENU_ORDER.ai}>
      <SidebarMenuButton
        onClick={hasSurface ? () => toggle() : undefined}
        disabled={!hasSurface}
        isActive={panelActive}
        className="cursor-pointer"
        aria-label={gt('Open AI assistant', {
          context: 'sidebar help menu button aria label for AI assistant',
        })}
        tooltip={hasSurface ? enabledTooltip : disabledTooltip}
      >
        <SparkleIcon weight="fill" className="text-purple-600" />
        {/* Trailing label/shortcut block — hidden in icon-mode so the
            SidebarMenuButton can center the icon inside its 32×32 shell. */}
        <span className="flex w-full items-center justify-between gap-2 group-data-[collapsible=icon]:hidden">
          <T context="sidebar help menu item label for opening the AI assistant">
            AI assistant
          </T>
          <kbd className="rounded border border-sidebar-border/60 bg-sidebar-accent/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            {shortcutLabel}
          </kbd>
        </span>
      </SidebarMenuButton>
    </HelpMenuSlot>
  );
}
